import type { CSSProperties, ReactNode } from "react";
import { Icon } from "./Icon";
import { type MockUser } from "../data";

/** Sticker shape compatible con mock (data.ts) y API (api/catalog.ts).
 *  Ambos tienen los mismos campos relevantes; pero el mock usa `team` (string) y el
 *  API usa `team_id` (number). Para color del equipo, los componentes nuevos pasan el
 *  color directamente como prop. */
export interface StickerLike {
  id: number;
  num: string;
  name: string;
  role: string;
  shirt?: number | null;
  isShield?: boolean;
  is_shield?: boolean;
  legendary?: boolean;
  is_legendary?: boolean;
  color?: string | null;
  image_url?: string | null;
  team?: string;        // mock only
  team_id?: number;     // API only
}

export function glyphFor(sticker: StickerLike): string | number {
  const isShield = sticker.isShield ?? sticker.is_shield;
  if (isShield) return (sticker.team || "").slice(0, 3) || "★";
  if (sticker.role === "Estadio") return "🏟";
  if (sticker.role === "Mascota") return "★";
  if (sticker.role === "Trofeo") return "🏆";
  return sticker.shirt ?? "?";
}

/** Iniciales del nombre del jugador para el placeholder visual. "Brahim Díaz" → "BD". */
export function playerInitials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "·";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  // Tomar inicial del primer y último componente (ignora middle names)
  const first = parts[0][0];
  const last = parts[parts.length - 1][0];
  return (first + last).toUpperCase();
}

interface StickerCardProps {
  sticker: StickerLike;
  /** Color del equipo (hex) — el caller lo conoce porque ya tiene los teams cargados. */
  teamColor?: string;
  /** Código corto del equipo (3 letras) para mostrar en el footer. */
  teamCode?: string;
  count?: number;
  variant?: "default" | "flat" | "photo";
  showDup?: boolean;
  onClick?: () => void;
}

export function StickerCard({ sticker, teamColor, teamCode, count = 0, variant = "default", showDup = true, onClick }: StickerCardProps) {
  const color = sticker.color || teamColor || "#10b981";
  const code = teamCode || sticker.team || "";
  const isLegendary = sticker.legendary ?? sticker.is_legendary;
  const empty = count === 0;
  const dup = count >= 2;
  const cls = ["sticker"];
  if (empty) cls.push("empty");
  else cls.push("owned");
  if (dup) cls.push("duplicate");
  if (isLegendary) cls.push("legendary");
  if (variant === "flat") cls.push("variant-flat");
  if (variant === "photo") cls.push("variant-photo");

  const styleVars = { "--team-color": color } as CSSProperties;

  if (empty) {
    return (
      <div className={cls.join(" ")} style={styleVars} onClick={onClick}>
        <div className="empty-number">{sticker.num}</div>
      </div>
    );
  }

  // Parsear num formato "ECU-18" → mostrar como "ECU 18" en el foot.
  // Si no tiene guión (legacy), mostrar tal cual.
  const [parsedCode, parsedPos] = (sticker.num || "").split("-");
  const footLabel = parsedPos ? `${parsedCode} ${parsedPos}` : (code || sticker.num || "");

  return (
    <div className={cls.join(" ")} style={styleVars} onClick={onClick}>
      {isLegendary && <div className="leg-glow" />}
      {dup && showDup && <div className="dup-pill">×{count}</div>}
      <div className="sticker-body">
        <div className="player-glyph">{glyphFor(sticker)}</div>
        <div className="player-name">{sticker.name}</div>
      </div>
      <div className="sticker-foot">
        <div className="flag" />
        <span>{footLabel}</span>
      </div>
    </div>
  );
}

/** Acepta tanto MockUser (camelCase) como UserMe del backend (snake_case). */
type AvatarUserLike = {
  initials: string;
  avatarColor?: string;
  avatar_color?: string;
  verified?: boolean;
};

interface AvatarProps {
  user?: AvatarUserLike | MockUser | null;
  size?: "md" | "lg" | "xl";
}

export function Avatar({ user, size = "md" }: AvatarProps) {
  if (!user) return null;
  const color = (user as AvatarUserLike).avatarColor || (user as AvatarUserLike).avatar_color || "#10b981";
  const cls = ["avatar"];
  if (size === "lg") cls.push("lg");
  if (size === "xl") cls.push("xl");
  return (
    <div className={cls.join(" ")} style={{ background: color, color: "white" }}>
      {user.initials}
      {user.verified && (
        <div className="verify-dot">
          <Icon name="check" size={8} />
        </div>
      )}
    </div>
  );
}

interface StarsProps {
  value?: number;
  size?: number;
}

export function Stars({ value = 0, size = 14 }: StarsProps) {
  const full = Math.floor(value);
  const half = value % 1 >= 0.4;
  return (
    <span className="profile-stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} style={{ opacity: i < full ? 1 : i === full && half ? 0.6 : 0.25 }}>
          <Icon name="star" size={size} />
        </span>
      ))}
    </span>
  );
}

/** Slot del álbum: misma estética que la vista libro (números camiseta grandes,
 *  fondo coloreado, badge de repetida, marker legendario). Usable en cualquier grid. */
interface BookSlotProps {
  sticker: StickerLike;
  count: number;
  teamColor: string;
  onClick?: () => void;
  /** Right-click (desktop) o long-press (mobile) — abre prompt para tipear N exacto. */
  onLongPress?: () => void;
  /** Ajuste rápido +/-1 (sumar / restar copia). Si está, se muestra un mini
   *  control de cantidad cuando count >= 1 con botones grandes tappeables. */
  onAdjust?: (delta: number) => void;
  /** Si está, muestra botón 📸 en esquina cuando count >= 1. */
  onPhotoClick?: () => void;
  /** URL preferida (foto primary subida por admin). Tiene prioridad sobre sticker.image_url. */
  primaryPhotoUrl?: string;
}

export function BookSlot({ sticker, count, teamColor, onClick, onLongPress, onAdjust, onPhotoClick, primaryPhotoUrl }: BookSlotProps) {
  const empty = count === 0;
  const dup = count >= 2;
  const isShield = sticker.is_shield ?? sticker.isShield;
  const isLegendary = sticker.is_legendary ?? sticker.legendary;

  const [code, posStr] = (sticker.num || "").split("-");
  const displayNum = posStr ? `${code} ${posStr}` : sticker.num;

  const cls = ["book-slot"];
  if (empty) cls.push("empty");
  else cls.push("owned");
  if (dup) cls.push("duplicate");
  if (isLegendary) cls.push("legendary");
  if (isShield) cls.push("shield");

  // Si la lámina está pegada (count >= 1) y hay imagen, mostrar imagen real.
  // Prioridad: primaryPhotoUrl (admin) > sticker.image_url (catálogo) > fallback.
  const imageToShow = primaryPhotoUrl || sticker.image_url;
  const showImage = !empty && imageToShow;

  return (
    <button
      type="button"
      className={cls.join(" ")}
      onClick={onClick}
      onContextMenu={onLongPress ? (e) => { e.preventDefault(); onLongPress(); } : undefined}
      style={{ "--team-color": teamColor } as CSSProperties}
    >
      <div className="book-slot-num-top">{displayNum}</div>
      <div className="book-slot-content">
        {showImage ? (
          <img
            src={imageToShow!}
            alt={sticker.name}
            className="book-slot-img"
            loading="lazy"
            onError={(e) => {
              // Si la URL falla, ocultamos el img y mostramos fallback
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : isShield ? (
          <div className="book-slot-shield-text">{(code || "").slice(0, 3)}</div>
        ) : sticker.role === "Foto grupal" ? (
          <div className="book-slot-icon">👥</div>
        ) : sticker.role === "Estadio" ? (
          <div className="book-slot-icon">🏟️</div>
        ) : sticker.role === "Mascota" ? (
          <div className="book-slot-icon">★</div>
        ) : sticker.role === "Trofeo" ? (
          <div className="book-slot-icon">🏆</div>
        ) : (
          <div className="book-slot-shirt-num" style={{ color: teamColor }}>
            {playerInitials(sticker.name)}
          </div>
        )}
      </div>
      {count >= 1 && onAdjust ? (
        /* Reemplaza el nombre por un control de cantidad compacto en la
         *  parte inferior del slot. Botones grandes (28×24) tappeables. */
        <div className="book-slot-qty" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="book-slot-qty-btn"
            onClick={(e) => { e.stopPropagation(); onAdjust(-1); }}
            aria-label="Restar una"
            title={count === 1 ? "Marcar como vacía" : "Tengo una menos"}
          >−</button>
          <button
            type="button"
            className="book-slot-qty-num"
            onClick={(e) => { e.stopPropagation(); onLongPress?.(); }}
            aria-label="Editar cantidad"
            title="Click para escribir cantidad exacta"
          >
            {count}
          </button>
          <button
            type="button"
            className="book-slot-qty-btn"
            onClick={(e) => { e.stopPropagation(); onAdjust(1); }}
            aria-label="Sumar una"
            title="Tengo una más"
          >+</button>
        </div>
      ) : (
        <div className="book-slot-name">
          {sticker.role === "Foto grupal" ? "Plantel" : sticker.name}
        </div>
      )}
      {/* Badge ×N solo cuando count >= 2 y no hay control activo (ej. perfil
       *  ajeno o vista read-only). Si onAdjust está, el control del foot
       *  reemplaza este badge. */}
      {dup && !onAdjust && (
        onLongPress ? (
          <span
            className="book-slot-dup"
            role="button"
            onClick={(e) => { e.stopPropagation(); onLongPress(); }}
            title="Click para escribir cantidad exacta"
            style={{ cursor: "pointer" }}
          >
            ×{count}
          </span>
        ) : (
          <div className="book-slot-dup">×{count}</div>
        )
      )}
      {isLegendary && <div className="book-slot-leg-marker">★</div>}
      {isLegendary && !empty && <div className="book-slot-shine" />}
      {!empty && onPhotoClick && (
        <span
          className="book-slot-photo-btn"
          onClick={(e) => { e.stopPropagation(); onPhotoClick(); }}
          role="button"
          aria-label="Ver fotos de esta lámina"
          title="Ver fotos / subir mi foto"
        >
          📸
        </span>
      )}
    </button>
  );
}

interface EmptyStateProps {
  icon?: string;
  title: string;
  body: string;
  action?: ReactNode;
}

export function EmptyState({ icon = "sparkle", title, body, action }: EmptyStateProps) {
  return (
    <div className="empty-state">
      <div style={{ display: "inline-grid", placeItems: "center", width: 56, height: 56, borderRadius: "50%", background: "var(--bg-subtle)", color: "var(--fg-muted)" }}>
        <Icon name={icon} size={28} />
      </div>
      <h3>{title}</h3>
      <p style={{ maxWidth: 320, margin: "0 auto" }}>{body}</p>
      {action}
    </div>
  );
}
